// @flow
import React, {useState} from 'react';
import {capitalize} from 'lodash';
import Box from 'sail/Box';
import Checkbox from 'sail/Checkbox';
import Group from 'sail/Group';
import Radio from 'sail/Radio';
import {Button} from 'sail/Button';
import Select, {SelectOption} from 'sail/Select';
import {BodyAlt, Text, Heading, Caption} from 'sail/Text';
import {TextArea, TextInput} from 'sail/Input';
import {defineMessages, FormattedMessage} from 'react-intl';
import {getUserOS, sanitizePII} from 'docs/util';
import {useAccount, useAnalytics, useApi, useArticle} from 'docs/data';

const SUBMISSION_STATUS = Object.freeze({
  INITIAL: 'initial',
  SUBMITTED: 'submitted',
  NO_RAING: 'no-rating',
  NO_FEEDBACK: 'no-feedback',
});

export const DEV_TOOLS = Object.freeze({
  STRIPE_CLI: 'Stripe CLI',
  STRIPE_VSCODE: 'Stripe for Visual Studio Code',
  DOCUMENTATION: 'Stripe Docs',
  STRIPE_SHELL: 'Stripe Shell',
  WEBHOOKS: 'Webhooks',
  SAMPLES: 'Samples',
  OTHER: 'Other',
});

const devToolOptions = [];
for (const prop in DEV_TOOLS) {
  if (DEV_TOOLS[prop]) {
    devToolOptions.push(DEV_TOOLS[prop]);
  }
}

const ratingOptions = [...Array(10).keys()].map((x) => x + 1);

const messages = defineMessages({
  devToolsSelection: {
    id: 'docsApp.components.devToolsCsat.devToolsSelection',
    description: 'Label for developer tool selection',
    defaultMessage:
      'Select the developer tool you would like to provide feedback on',
  },
  vscodeVersion: {
    id: 'docsApp.components.devToolsCsat.vscodeVersion',
    description: 'Label for VSCode version',
    defaultMessage: 'Visual Studio Code version',
  },
  extensionVersion: {
    id: 'docsApp.components.devToolsCsat.extensionVersion',
    description: 'Label for Stripe extension version',
    defaultMessage: 'Stripe extension version',
  },
  machineId: {
    id: 'docsApp.components.devToolsCsat.machineId',
    description: 'Label for machine ID',
    defaultMessage: 'Machine ID',
  },
  devToolsRating: {
    id: 'docsApp.components.devToolsCsat.devToolsRating',
    description: 'Label for rating',
    defaultMessage: 'Rate your experience with this developer tool',
  },
  dissatisfied: {
    id: 'docsApp.components.devToolsCsat.dissatisfied',
    description: 'Label for dissatisfied',
    defaultMessage: 'Extremely dissatisfied',
  },
  satisfied: {
    id: 'docsApp.components.devToolsCsat.satisfied',
    description: 'Label satisfied',
    defaultMessage: 'Extremely satisfied',
  },
  feedbacks: {
    id: 'docsApp.components.devToolsCsat.feedbacks',
    description: 'Label for feedbacks',
    defaultMessage: 'Tell us why you chose this rating',
  },
  contactPrompt: {
    id: 'docsApp.components.devToolsCsat.contactPrompt',
    description: 'Label for contact prompt',
    defaultMessage:
      'May we contact you for questions? If so, please provide your name and email',
  },
  contactFullname: {
    id: 'docsApp.components.devToolsCsat.contactFullName',
    description: 'Label for full name',
    defaultMessage: 'Full Name',
  },
  contactEmail: {
    id: 'docsApp.components.devToolsCsat.contactEmail',
    description: 'Label for email',
    defaultMessage: 'Email',
  },
  okToFollowUp: {
    id: 'docsShell.csat.okToFollowUp',
    description: 'Asking if ok to follow up on CSAT responses by email',
    defaultMessage: "Yes, it's okay to follow up by email",
  },
  thankYouMessage: {
    id: 'docsApp.components.devToolsCsat.thankYouMessage',
    description: 'Thank you message',
    defaultMessage:
      'Thank you for helping us to improve Stripe developer tools!',
  },
  noRatingError: {
    id: 'docsApp.components.devToolsCsat.noRatingError',
    description: 'No rating provided error',
    defaultMessage: 'Please select a rating.',
  },
  noFeedbackError: {
    id: 'docsApp.components.devToolsCsat.noFeedbackError',
    description: 'No feedback provided error',
    defaultMessage: 'Tell us why you chose this rating.',
  },
  submit: {
    id: 'docsApp.components.devToolsCsat.submit',
    description: 'Label on submit button',
    defaultMessage: 'Submit',
  },
});

type DeveloperToolsCsatProps = {|
  devTool?: string,
  onClose?: () => void,
|};

export const DeveloperToolsCsat = (props: DeveloperToolsCsatProps) => {
  const urlParams =
    typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search)
      : undefined;

  let prefilledTool = props.devTool || DEV_TOOLS.STRIPE_CLI;
  let prefilledVscodeVer = '';
  let prefilledExtensionVer = '';
  let prefilledMachineId = '';

  if (urlParams) {
    if (urlParams.get('devTool')) {
      prefilledTool = urlParams.get('devTool');
      if (prefilledTool === 'cli') {
        prefilledTool = DEV_TOOLS.STRIPE_CLI;
      } else if (prefilledTool === 'vscode') {
        prefilledTool = DEV_TOOLS.STRIPE_VSCODE;
      } else if (prefilledTool === 'shell') {
        prefilledTool = DEV_TOOLS.STRIPE_SHELL;
      }
    }
    prefilledVscodeVer = urlParams.get('vscodeVer') || '';
    prefilledExtensionVer = urlParams.get('extensionVer') || '';
    prefilledMachineId = urlParams.get('machineId') || '';
  }

  const [pending, setPending] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState(
    SUBMISSION_STATUS.INITIAL,
  );

  // common to all tools
  const [devTool, setDevTool] = useState(prefilledTool);
  const [otherTool, setOtherTool] = useState('');
  const [rating, setRating] = useState(0);
  const [text, setText] = useState('');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [canContact, setCanContact] = useState(false);

  // vscode extension values
  const [vscodeVersion, setVscodeVersion] = useState(prefilledVscodeVer);
  const [extensionVersion, setExtensionVersion] = useState(
    prefilledExtensionVer,
  );
  const [machineId, setMachineId] = useState(prefilledMachineId);

  const {account} = useAccount();
  const {trackAction} = useAnalytics();
  const {article} = useArticle();
  const {api} = useApi();

  const userLoggedIn = account.user && account.user.email;

  const handleSubmit = async () => {
    if (rating === 0) {
      setSubmissionStatus(SUBMISSION_STATUS.NO_RAING);
      return;
    }

    if (text.trim().length < 1) {
      setSubmissionStatus(SUBMISSION_STATUS.NO_FEEDBACK);
      return;
    }

    if (userLoggedIn && canContact) {
      const accountEmail = account.user?.email ? account.user?.email : '';
      setFullName('');
      setEmail(accountEmail);
    }

    const params = {
      devTool,
      otherTool,
      vscodeVersion,
      extensionVersion,
      machineId,
      rating,
      fullName,
      email,
      os: capitalize(getUserOS()),
      text: sanitizePII(text, '[REDACTED]'),
      path: window.location.pathname,
      referrer: document.referrer,
      translated: article.intl.translated,
      locale: article.intl.locale,
    };

    setPending(true);
    await api.sendCsatFeedback(params);
    trackAction('dev_tools_csat', {
      ...params,
    });
    setPending(false);
    setSubmissionStatus(SUBMISSION_STATUS.SUBMITTED);

    if (props.onClose) {
      props.onClose();
    }
  };

  return (
    <form onFocus={(event) => event.stopPropagation()}>
      <Box padding={{bottom: 12}}>
        <Heading>
          <FormattedMessage {...messages.devToolsSelection} />
        </Heading>
        <Box padding={{vertical: 12}}>
          <Select
            required
            size="large"
            value={devTool}
            onChange={setDevTool}
            disabled={!!props.devTool}
          >
            {devToolOptions.map((item) => (
              <SelectOption key={item} value={item} label={item} />
            ))}
          </Select>
        </Box>
        {devTool === DEV_TOOLS.OTHER && (
          <Box padding={{vertical: 12}}>
            <TextArea
              onChange={setOtherTool}
              placeholder="Please specify"
              resizeable={false}
              value={otherTool}
              width="maximized"
            />
          </Box>
        )}
        {devTool === DEV_TOOLS.STRIPE_VSCODE && (
          <>
            <Box padding={{vertical: 12}}>
              <Text color="dark" size={15} weight="bold">
                <FormattedMessage {...messages.vscodeVersion} />
              </Text>
              <Box padding={{vertical: 12}}>
                <TextInput
                  size="large"
                  onChange={setVscodeVersion}
                  value={vscodeVersion}
                />
              </Box>
            </Box>
            <Box padding={{vertical: 12}}>
              <Text color="dark" size={15} weight="bold">
                <FormattedMessage {...messages.extensionVersion} />
              </Text>
              <Box padding={{vertical: 12}}>
                <TextInput
                  size="large"
                  onChange={setExtensionVersion}
                  value={extensionVersion}
                />
              </Box>
            </Box>
            <Box padding={{vertical: 12}}>
              <Text color="dark" size={15} weight="bold">
                <FormattedMessage {...messages.machineId} />
              </Text>
              <Box padding={{vertical: 12}}>
                <TextInput
                  size="large"
                  onChange={setMachineId}
                  value={machineId}
                />
              </Box>
            </Box>
          </>
        )}
      </Box>
      <Box padding={{vertical: 12}}>
        <Heading>
          <FormattedMessage {...messages.devToolsRating} />
        </Heading>
        <Group
          style={{
            display: 'flex',
            flexFlow: 'row nowrap',
            justifyContent: 'space-between',
            padding: '12px 0',
          }}
          spacing={16}
          direction="horizontal"
        >
          <Caption>
            <FormattedMessage {...messages.dissatisfied} />
          </Caption>
          {ratingOptions.map((ratingOption) => (
            <Radio
              checked={rating === ratingOption}
              onChange={() => setRating(ratingOption)}
              label={<Caption>{ratingOption}</Caption>}
              description=""
              key={ratingOption}
            />
          ))}
          <Caption>
            <FormattedMessage {...messages.satisfied} />
          </Caption>
        </Group>
      </Box>
      <Box padding={{vertical: 12}}>
        <Heading>
          <FormattedMessage {...messages.feedbacks} />
        </Heading>
        <Box padding={{vertical: 12}}>
          <TextArea
            onChange={setText}
            placeholder=""
            resizeable={false}
            value={text}
            width="maximized"
            size="large"
            required
          />
        </Box>
      </Box>

      {userLoggedIn ? (
        <Box padding={{vertical: 12}}>
          <Checkbox
            checked={canContact}
            onChange={({target}) => {
              setCanContact(target.checked);
            }}
            label={<FormattedMessage {...messages.okToFollowUp} />}
          />
        </Box>
      ) : (
        <Box padding={{vertical: 12}}>
          <Heading>
            <FormattedMessage {...messages.contactPrompt} />
          </Heading>
          <Box padding={{vertical: 12}}>
            <Group spacing={20}>
              <Group spacing={{vertical: 8}}>
                <label>
                  <BodyAlt>Full name</BodyAlt>
                </label>
                <TextInput
                  size="large"
                  width="long"
                  onChange={setFullName}
                  value={fullName}
                />
              </Group>
              <Group spacing={{vertical: 8}}>
                <label>
                  <BodyAlt>Email</BodyAlt>
                </label>
                <TextInput
                  size="large"
                  width="long"
                  onChange={setEmail}
                  value={email}
                />
              </Group>
            </Group>
          </Box>
        </Box>
      )}
      <Button
        label={<FormattedMessage {...messages.submit} />}
        color="blue"
        width="short"
        onClick={handleSubmit}
        pending={pending}
      />
      {submissionStatus === SUBMISSION_STATUS.SUBMITTED && (
        <Box margin={{vertical: 12}}>
          <FormattedMessage {...messages.thankYouMessage} />
        </Box>
      )}
      {submissionStatus === SUBMISSION_STATUS.NO_RAING && (
        <Box margin={{vertical: 12}}>
          <FormattedMessage {...messages.noRatingError} />
        </Box>
      )}
      {submissionStatus === SUBMISSION_STATUS.NO_FEEDBACK && (
        <Box margin={{vertical: 12}}>
          <FormattedMessage {...messages.noFeedbackError} />
        </Box>
      )}
    </form>
  );
};
